<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'

const props = defineProps<{
  answeredQuestions: number
  totalQuestions: number
}>()

// Animierte Werte für sanfte Übergänge
const animatedValue = ref(0)
const animatedPercentage = ref(0)

// Berechnete Werte
const percentage = computed(() =>
  props.totalQuestions > 0 ? (props.answeredQuestions / props.totalQuestions) * 100 : 0,
)

// Animation für Fortschrittsbalken
const animateProgress = async () => {
  const startValue = animatedValue.value
  const startPercentage = animatedPercentage.value
  const targetValue = props.answeredQuestions
  const targetPercentage = percentage.value

  const duration = 800 // 800ms Animation
  const steps = 30
  const stepTime = duration / steps

  const valueStep = (targetValue - startValue) / steps
  const percentageStep = (targetPercentage - startPercentage) / steps

  for (let i = 0; i <= steps; i++) {
    setTimeout(() => {
      animatedValue.value = Math.round(startValue + valueStep * i)
      animatedPercentage.value = Math.round(startPercentage + percentageStep * i)
    }, stepTime * i)
  }
}

// Watcher für Props-Änderungen
watch(
  [() => props.answeredQuestions, () => props.totalQuestions],
  () => {
    nextTick(() => {
      animateProgress()
    })
  },
  { immediate: true },
)
</script>

<template>
  <div class="progress-bar mb-4">
    <div class="progress-container">
      <progress
        class="progress is-primary animated-progress mb-0"
        :value="animatedValue"
        :max="props.totalQuestions"
      >
        {{ animatedPercentage }}%
      </progress>

      <!-- Glowing Effect -->
      <div class="progress-glow" :style="{ width: animatedPercentage + '%' }"></div>
    </div>

    <div class="progress-info">
      <div class="is-size-7 has-text-centered mt-1">
        <span class="animated-number">{{ animatedPercentage }}</span
        >% abgeschlossen
      </div>
      <div class="is-size-8 has-text-grey has-text-centered">
        {{ animatedValue }} von {{ props.totalQuestions }} Fragen
      </div>
    </div>
  </div>
</template>

<style scoped>
.progress-bar {
  position: relative;
}

.progress-container {
  position: relative;
  overflow: hidden;
  border-radius: 0.75rem;
}

.animated-progress {
  height: 1.5rem;
  border-radius: 0.75rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

/* Moderne CSS Fortschrittsbalken Animation */
.animated-progress::-webkit-progress-bar {
  background-color: var(--bulma-grey-lighter);
  border-radius: 0.75rem;
}

.animated-progress::-webkit-progress-value {
  background: linear-gradient(
    45deg,
    var(--primary-color, var(--secondary-color)) 0%,
    var(--primary-color, var(--secondary-color)) 50%,
    rgba(var(--primary-color-rgb, 50, 115, 220), 0.8) 100%
  );
  border-radius: 0.75rem;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  animation: progressShine 2s infinite;
}

.animated-progress::-moz-progress-bar {
  background: linear-gradient(
    45deg,
    var(--primary-color, var(--secondary-color)) 0%,
    var(--primary-color, var(--secondary-color)) 50%,
    rgba(var(--primary-color-rgb, 50, 115, 220), 0.8) 100%
  );
  border-radius: 0.75rem;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Glowing Effect */
.progress-glow {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(var(--primary-color-rgb, 50, 115, 220), 0.3) 50%,
    transparent 100%
  );
  border-radius: 0.75rem;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  animation: progressGlow 3s ease-in-out infinite alternate;
}

/* Animierte Zahl */
.animated-number {
  font-weight: 600;
  color: var(--primary-color);
  transition: all 0.3s ease;
}

.progress-info {
  margin-top: 0.5rem;
}

/* Keyframes für Animationen */
@keyframes progressShine {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes progressGlow {
  0% {
    opacity: 0.3;
    transform: translateX(-10px);
  }
  100% {
    opacity: 0.6;
    transform: translateX(10px);
  }
}

/* Responsive Anpassungen */
@media (max-width: 768px) {
  .animated-progress {
    height: 1.25rem;
  }

  .progress-info .is-size-7 {
    font-size: 0.75rem;
  }

  .progress-info .is-size-8 {
    font-size: 0.625rem;
  }
}
</style>
